// Global Instructions Rule Applied!
//
// Who may move a deal or a prospect into or out of a pipeline stage, applied to
// a stage picker (docs/USER_RIGHTS_DESIGN.md 6.1; capability pipeline_stage.move,
// one rule per pipeline_config row - the stage option's value).
//
// Every stage menu builds its options its own way (the deal and prospect
// overviews, both New drawers, both grids); each passes them through here:
//   * a stage the user may not ENTER is disabled, its reason on hover;
//   * when the user may not LEAVE the record's current stage, every other
//     stage is disabled with that reason - the record stays where it is;
//   * the current stage itself is never disabled (it is the value shown).
// The boards ask the same question per column (useCapabilityCheck).
//
// A display hint only: the stage guard in the database refuses the move
// whatever the screen shows (trg_pipeline__guard_stage_move).
import { useCallback } from 'react'
import { useCapabilityCheck, type CapabilityCheck, type CapabilityChecker } from './useCapability.ts'

export const CAPABILITY_PIPELINE_STAGE_MOVE = 'pipeline_stage.move'

export interface StageOptionLike {
  value: string | number
  label: string
}

// What the rights add to an option the user may not pick.
export interface StageMoveRightsMarks {
  disabled?: boolean
  title?: string
}

const ALLOWED: CapabilityCheck = { allowed: true, reason: null }

const sameStage = (a: string | number | null | undefined, b: string | number | null | undefined): boolean =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b)

export function applyStageMoveRights<T extends StageOptionLike>(
  options: T[],
  currentStageId: string | number | null | undefined,
  check: CapabilityChecker
): Array<T & StageMoveRightsMarks> {
  const current = options.find((option) => sameStage(option.value, currentStageId))
  const leave =
    currentStageId === null || currentStageId === undefined || currentStageId === ''
      ? ALLOWED
      : check(CAPABILITY_PIPELINE_STAGE_MOVE, String(currentStageId), current?.label ?? null)

  return options.map((option) => {
    if (sameStage(option.value, currentStageId)) return option
    const blocked = leave.allowed ? check(CAPABILITY_PIPELINE_STAGE_MOVE, String(option.value), option.label) : leave
    return blocked.allowed ? option : { ...option, disabled: true, title: blocked.reason ?? undefined }
  })
}

export type StageMoveRightsApplier = <T extends StageOptionLike>(
  options: T[],
  currentStageId: string | number | null | undefined
) => Array<T & StageMoveRightsMarks>

export function useStageMoveRights(): StageMoveRightsApplier {
  const check = useCapabilityCheck()
  return useCallback<StageMoveRightsApplier>(
    (options, currentStageId) => applyStageMoveRights(options, currentStageId, check),
    [check]
  )
}
